import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Skeleton,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, Refresh } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  useCreateClientMutation,
  useUpdateClientMutation,
  useArchiveClientMutation,
  useUpdateAdminUserMutation,
  useGetAdminUsersQuery,
  useLazyCheckClientCodeAvailabilityQuery,
  useLazyCheckClientNameAvailabilityQuery,
} from "features/adminUsers/adminUsers";
import { useAdminUsersAutocompleteInfinite } from "features/adminUsers/useAdminUsersAutocompleteInfinite";
import {
  platformUserAutocompleteComponentsProps,
  PlatformUserAutocompleteListbox,
  PlatformUserLabelBlock,
  renderPlatformUserAutocompleteOption,
  formatPlatformUserOptionLabel,
} from "components/common/PlatformUserLabelBlock";
import { useAppDispatch, useAppSelector } from "features/store";
import { pushNotification } from "features/notificationSlice";
import { Client, ClientOwner, PlatformUser } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import UnsavedPlatformDraftsDialog from "./UnsavedPlatformDraftsDialog";
import type { PlatformDraftSummaryRow } from "userManagement/platformDraftsStorage";
import AppDialog from "../common/AppDialog";
import type { ConfirmAccessChangesSection } from "./UserManagementPanel";

interface ClientEditModalProps {
  open: boolean;
  client: Client | null;
  onClose: () => void;
  onSaved: (client: Client) => void;
  onUnsavedChange?: (dirty: boolean) => void;
  onUnsavedSummaryChange?: (summary: string[]) => void;
}

type TabId = "info" | "ownership" | "users";

function generateCode(): string {
  const digits = "23456789";
  const letters = "ABCDEFGHJKMNPQRSTUVWXYZ";
  const d = Array.from({ length: 3 }, () =>
    digits.charAt(Math.floor(Math.random() * digits.length)),
  ).join("");
  const l = Array.from({ length: 3 }, () =>
    letters.charAt(Math.floor(Math.random() * letters.length)),
  ).join("");
  return d + l;
}

export default function ClientEditModal({
  open,
  client,
  onClose,
  onSaved,
  onUnsavedChange,
  onUnsavedSummaryChange,
}: ClientEditModalProps) {
  const isCreate = client === null;
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);
  const isSuperUser = Boolean(currentUser?.platform_profile?.is_superuser);

  const [tab, setTab] = useState<TabId>("info");

  const [companyName, setCompanyName] = useState("");
  const [companyNameServerError, setCompanyNameServerError] = useState<
    string | null
  >(null);
  const [companyCode, setCompanyCode] = useState("");
  const [companyCodeServerError, setCompanyCodeServerError] = useState<
    string | null
  >(null);
  const [isActive, setIsActive] = useState(true);
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [addUserSearch, setAddUserSearch] = useState("");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveDialogDate, setArchiveDialogDate] = useState<Date | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [saveConfirmSections, setSaveConfirmSections] = useState<
    ConfirmAccessChangesSection[]
  >([]);
  const [pendingAddedUsers, setPendingAddedUsers] = useState<PlatformUser[]>(
    [],
  );

  const {
    options: ownerOptions,
    loading: ownersLoading,
    onListboxScroll: ownersListboxOnScroll,
  } = useAdminUsersAutocompleteInfinite({
    skip: !open || tab !== "ownership",
    page_size: 50,
    search: ownerSearch,
    sort_by: "name",
    sort_order: "asc",
    user_type: "internal",
  });

  const {
    options: allClientUserOptions,
    loading: addUserLoading,
    onListboxScroll: addUserListboxOnScroll,
  } = useAdminUsersAutocompleteInfinite({
    skip: !open || tab !== "users",
    page_size: 50,
    search: addUserSearch,
    sort_by: "name",
    sort_order: "asc",
    user_type: "client",
  });

  const { data: clientUsersData, isLoading: isLoadingClientUsers } =
    useGetAdminUsersQuery(
      { client_id: client?.id, user_type: "client", page_size: 200 },
      { skip: tab !== "users" || isCreate || !client?.id },
    );
  const clientUsers = clientUsersData?.results ?? [];

  const assignedUserIds = useMemo(
    () =>
      new Set([
        ...clientUsers.map((u) => u.id),
        ...pendingAddedUsers.map((u) => u.id),
      ]),
    [clientUsers, pendingAddedUsers],
  );

  const addUserOptions = useMemo(
    () => allClientUserOptions.filter((u) => !assignedUserIds.has(u.id)),
    [allClientUserOptions, assignedUserIds],
  );

  const selectedOwners: PlatformUser[] = useMemo(
    () => ownerOptions.filter((u) => ownerIds.includes(u.id)),
    [ownerOptions, ownerIds],
  );

  useEffect(() => {
    if (!open) return;
    setTab("info");
    setArchiveDialogOpen(false);
    setArchiveDialogDate(null);
    setOwnerSearch("");
    setAddUserSearch("");
    setPendingAddedUsers([]);
    if (client) {
      setCompanyName(client.company_name);
      setCompanyCode(client.company_code);
      setIsActive(client.is_active);
      setOwnerIds(client.calibrate_owners.map((o) => o.id));
    } else {
      setCompanyName("");
      setCompanyCode(generateCode());
      setIsActive(true);
      setOwnerIds([]);
    }
  }, [open, client]);

  const handleArchiveNow = async () => {
    if (!client) return;
    try {
      const saved = await archiveClient({ id: client.id }).unwrap();
      showToast("Client archived.", "success");
      onSaved(saved);
      setArchiveDialogOpen(false);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to archive client.", "error");
    }
  };

  const handleScheduleArchive = async () => {
    if (!client || !archiveDialogDate) return;
    const d = archiveDialogDate;
    const dateStr = [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, "0"),
      String(d.getDate()).padStart(2, "0"),
    ].join("-");
    try {
      const saved = await archiveClient({
        id: client.id,
        archiveDate: dateStr,
      }).unwrap();
      showToast(`Archive scheduled for ${dateStr}.`, "success");
      onSaved(saved);
      setArchiveDialogOpen(false);
      setArchiveDialogDate(null);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to schedule archive.", "error");
    }
  };

  const handleCancelSchedule = async () => {
    if (!client) return;
    try {
      const saved = await archiveClient({
        id: client.id,
        archiveDate: null,
      }).unwrap();
      showToast("Archive schedule cancelled.", "success");
      onSaved(saved);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to cancel schedule.", "error");
    }
  };

  const handleUnarchive = async () => {
    if (!client) return;
    try {
      const saved = await archiveClient({ id: client.id }).unwrap();
      showToast("Client un-archived.", "success");
      onSaved(saved);
    } catch (err: any) {
      showToast(err?.data?.error || "Failed to un-archive client.", "error");
    }
  };

  const handleAddUser = (_: unknown, selectedUser: PlatformUser | null) => {
    if (!selectedUser || !client) return;
    setPendingAddedUsers((prev) =>
      prev.some((u) => u.id === selectedUser.id)
        ? prev
        : [...prev, selectedUser],
    );
    setAddUserSearch("");
  };

  const handleRemovePendingUser = (userId: number) => {
    setPendingAddedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const hasUnsavedChanges = useMemo(() => {
    if (isCreate) {
      return (
        companyName.trim() !== "" ||
        !isActive ||
        ownerIds.length > 0 ||
        pendingAddedUsers.length > 0
      );
    }
    if (!client) return false;
    const baselineOwnerIds = client.calibrate_owners.map((o) => o.id).sort();
    const currentOwnerIds = [...ownerIds].sort();
    return (
      companyName.trim() !== (client.company_name ?? "").trim() ||
      companyCode.trim() !== (client.company_code ?? "").trim() ||
      isActive !== client.is_active ||
      JSON.stringify(baselineOwnerIds) !== JSON.stringify(currentOwnerIds) ||
      pendingAddedUsers.length > 0
    );
  }, [
    isCreate,
    client,
    companyName,
    companyCode,
    isActive,
    ownerIds,
    pendingAddedUsers,
  ]);

  const unsavedSummary = useMemo(() => {
    if (isCreate) {
      const sections: string[] = [];
      if (companyName.trim() !== "") sections.push("Company name");
      if (!isActive) sections.push("Status (inactive)");
      if (ownerIds.length > 0) sections.push("Owners");
      if (pendingAddedUsers.length > 0) sections.push("Users");
      return sections;
    }
    if (!client) return [];
    const sections: string[] = [];
    if (companyName.trim() !== (client.company_name ?? "").trim())
      sections.push("Company name");
    if (companyCode.trim() !== (client.company_code ?? "").trim())
      sections.push("Company code");
    if (isActive !== client.is_active) sections.push("Status");
    const baselineOwnerIds = client.calibrate_owners.map((o) => o.id).sort();
    const currentOwnerIds = [...ownerIds].sort();
    if (JSON.stringify(baselineOwnerIds) !== JSON.stringify(currentOwnerIds))
      sections.push("Owners");
    if (pendingAddedUsers.length > 0) sections.push("Users");
    return sections;
  }, [
    isCreate,
    client,
    companyName,
    companyCode,
    isActive,
    ownerIds,
    pendingAddedUsers,
  ]);

  useLayoutEffect(() => {
    onUnsavedChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChange]);

  useLayoutEffect(() => {
    onUnsavedSummaryChange?.(unsavedSummary);
  }, [unsavedSummary, onUnsavedSummaryChange]);

  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();
  const [archiveClient, { isLoading: isArchiving }] =
    useArchiveClientMutation();
  const [updateAdminUser, { isLoading: isUpdatingUser }] =
    useUpdateAdminUserMutation();
  const [checkClientCodeAvailability] =
    useLazyCheckClientCodeAvailabilityQuery();
  const [checkClientNameAvailability] =
    useLazyCheckClientNameAvailabilityQuery();
  const isSaving = isCreating || isUpdating || isArchiving || isUpdatingUser;

  const showToast = (message: string, severity: "success" | "error") =>
    dispatch(pushNotification({ message, severity }));

  const companyNameClientError =
    companyName.trim().length > 50
      ? "Company name cannot exceed 50 characters."
      : undefined;
  const companyNameError =
    companyNameClientError ?? companyNameServerError ?? undefined;
  const companyCodeInvalidChars = /[0O1LlIi]/.test(companyCode)
    ? "0, O, I, 1 and L are not allowed in company codes."
    : undefined;
  const companyCodeClientError =
    companyCode.trim().length === 0
      ? "Company Code is required."
      : companyCodeInvalidChars;
  const companyCodeError =
    companyCodeClientError ?? companyCodeServerError ?? undefined;

  const canSave =
    companyName.trim().length > 0 &&
    companyName.trim().length <= 50 &&
    !companyNameServerError &&
    companyCode.trim().length > 0 &&
    !companyCodeInvalidChars &&
    !companyCodeServerError;

  const performSave = async () => {
    if (!canSave) return;
    try {
      let saved: Client;
      if (isCreate) {
        saved = await createClient({
          company_name: companyName.trim(),
          company_code: companyCode.trim().toUpperCase(),
          calibrate_owner_ids: ownerIds,
        }).unwrap();
        showToast("Client created successfully.", "success");
      } else {
        saved = await updateClient({
          id: client!.id,
          company_name: companyName.trim(),
          company_code: companyCode.trim().toUpperCase(),
          calibrate_owner_ids: ownerIds,
          is_active: isActive,
        }).unwrap();
        showToast("Client updated successfully.", "success");
      }
      if (pendingAddedUsers.length > 0) {
        for (const pu of pendingAddedUsers) {
          try {
            await updateAdminUser({
              id: pu.id,
              client_id: saved.id,
            }).unwrap();
          } catch (err: any) {
            showToast(
              err?.data?.error || `Failed to assign user ${pu.username}.`,
              "error",
            );
          }
        }
      }
      onSaved(saved);
    } catch (err: any) {
      const detail =
        err?.data?.errors?.company_code?.[0] ||
        err?.data?.errors?.company_name?.[0] ||
        err?.data?.error ||
        "Failed to save client.";
      showToast(detail, "error");
    }
  };

  const buildSaveConfirmSections = (): ConfirmAccessChangesSection[] => {
    const sections: ConfirmAccessChangesSection[] = [];
    if (isCreate) {
      const added: string[] = [];
      if (companyName.trim()) added.push("Company name");
      if (companyCode.trim()) added.push("Company code");
      if (!isActive) added.push("Status (inactive)");
      if (added.length > 0) {
        sections.push({ label: "INFO", added });
      }
      if (ownerIds.length > 0) {
        sections.push({
          label: "OWNERS",
          added: [`${ownerIds.length} owner(s)`],
        });
      }
      if (pendingAddedUsers.length > 0) {
        sections.push({
          label: "USERS",
          added: pendingAddedUsers.map((u) => formatPlatformUserOptionLabel(u)),
        });
      }
      return sections;
    }
    if (!client) return sections;
    const updated: string[] = [];
    if (companyName.trim() !== (client.company_name ?? "").trim())
      updated.push("Company name");
    if (isActive !== client.is_active) updated.push("Status");
    if (updated.length > 0) {
      sections.push({ label: "INFO", updated });
    }
    const baselineOwnerIds = client.calibrate_owners.map((o) => o.id).sort();
    const currentOwnerIds = [...ownerIds].sort();
    if (JSON.stringify(baselineOwnerIds) !== JSON.stringify(currentOwnerIds)) {
      const addedOwners = ownerIds.filter(
        (id) => !baselineOwnerIds.includes(id),
      );
      const removedOwners = baselineOwnerIds.filter(
        (id) => !ownerIds.includes(id),
      );
      const ownerSection: ConfirmAccessChangesSection = { label: "OWNERS" };
      if (addedOwners.length > 0) {
        ownerSection.added = addedOwners.map((id) => {
          const o = ownerOptions.find((u) => u.id === id);
          return o ? formatPlatformUserOptionLabel(o) : `ID ${id}`;
        });
      }
      if (removedOwners.length > 0) {
        ownerSection.removed = removedOwners.map((id) => {
          const o = client.calibrate_owners.find((u) => u.id === id);
          if (!o) return `ID ${id}`;
          const name = [o.first_name, o.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
          return name ? `${name} @${o.username}` : `@${o.username}`;
        });
      }
      sections.push(ownerSection);
    }
    if (pendingAddedUsers.length > 0) {
      sections.push({
        label: "USERS",
        added: pendingAddedUsers.map((u) => formatPlatformUserOptionLabel(u)),
      });
    }
    return sections;
  };

  const handleSave = () => {
    if (!canSave) return;
    const sections = buildSaveConfirmSections();
    if (sections.length > 0) {
      setSaveConfirmSections(sections);
      setSaveConfirmOpen(true);
      return;
    }
    performSave();
  };

  const clientId = client ? client.id : undefined;

  useEffect(() => {
    const value = companyName.trim();
    if (!value) {
      setCompanyNameServerError(null);
      return;
    }
    if (client && value === client.company_name.trim()) {
      setCompanyNameServerError(null);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const res = await checkClientNameAvailability({
          value,
          excludeId: clientId,
        }).unwrap();
        if (cancelled) return;
        setCompanyNameServerError(
          res.available
            ? null
            : (res.message ?? "This company name is already in use."),
        );
      } catch {
        if (!cancelled) setCompanyNameServerError(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [companyName, client, clientId, checkClientNameAvailability]);

  useEffect(() => {
    const value = companyCode.trim();
    if (!value || !isCreate) {
      setCompanyCodeServerError(null);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        const res = await checkClientCodeAvailability({
          value,
          excludeId: clientId,
        }).unwrap();
        if (cancelled) return;
        setCompanyCodeServerError(
          res.available
            ? null
            : (res.message ?? "This company code is already in use."),
        );
      } catch {
        if (!cancelled) setCompanyCodeServerError(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [companyCode, isCreate, clientId, checkClientCodeAvailability]);

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          height: { xs: "90vh", sm: 840 },
          maxHeight: { xs: "90vh", sm: 840 },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0,
          pt: 2,
          px: 3,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="h6" fontWeight={700}>
            {isCreate ? "Add New Client" : `Edit: ${client!.company_name}`}
          </Typography>
          {client && client.is_archived && (
            <Chip
              label="ARCHIVED"
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                bgcolor: "rgba(211,47,47,0.10)",
                color: "error.dark",
              }}
            />
          )}
          {client && !client.is_archived && client.scheduled_archive_date && (
            <Chip
              label="SCHEDULED"
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                bgcolor: "rgba(255,152,0,0.12)",
                color: "warning.dark",
              }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} disabled={isSaving} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, borderBottom: "1px solid", borderColor: "divider" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as TabId)}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
          }}
        >
          <Tab label="Info" value="info" />
          <Tab
            label="Ownership"
            value="ownership"
            disabled={!isCreate && client?.is_archived}
          />
          {!isCreate && (
            <Tab label="Users" value="users" disabled={client?.is_archived} />
          )}
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, minHeight: 360 }}>
        <CustomScrollbars sx={{ height: 420 }}>
          <Box sx={{ px: 3, py: 2.5 }}>
            {tab === "info" && (
              <Box display="flex" flexDirection="column" gap={2.5}>
                <TextField
                  label="Company Name"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setCompanyNameServerError(null);
                  }}
                  fullWidth
                  required
                  autoFocus
                  size="small"
                  disabled={isSaving}
                  error={Boolean(companyNameError)}
                  helperText={companyNameError}
                />
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    label="Company Code"
                    value={companyCode}
                    size="small"
                    required
                    disabled={isSaving}
                    InputProps={{ readOnly: true }}
                    inputProps={{
                      maxLength: 10,
                      style: { fontFamily: "monospace", fontWeight: 700 },
                    }}
                    sx={{ flex: 1 }}
                    error={Boolean(companyCodeError)}
                    helperText={
                      companyCodeError ??
                      (isCreate
                        ? "Auto-generated \u2014 use refresh to get a new code"
                        : "Issued once at creation \u2014 cannot be changed")
                    }
                  />
                  {isCreate && (
                    <Tooltip title="Regenerate code">
                      <span>
                        <IconButton
                          onClick={() => setCompanyCode(generateCode())}
                          disabled={isSaving}
                          size="small"
                          sx={{ mt: -2.5 }}
                        >
                          <Refresh fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}

            {tab === "ownership" && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  Assign Calibrate internal users who own this client account.
                </Typography>
                <Autocomplete
                  multiple
                  options={ownerOptions}
                  loading={ownersLoading}
                  componentsProps={platformUserAutocompleteComponentsProps}
                  ListboxComponent={PlatformUserAutocompleteListbox}
                  ListboxProps={{ onScroll: ownersListboxOnScroll }}
                  value={selectedOwners}
                  onChange={(_, newValue) =>
                    setOwnerIds(newValue.map((v) => v.id))
                  }
                  getOptionLabel={formatPlatformUserOptionLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  disabled={isSaving || !isSuperUser}
                  renderOption={renderPlatformUserAutocompleteOption}
                  onInputChange={(_, value) => setOwnerSearch(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Calibrate Owners"
                      size="small"
                      placeholder="Search internal users…"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={
                          option.first_name || option.last_name
                            ? `${option.first_name ?? ""} ${option.last_name ?? ""}`.trim()
                            : option.username
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    ))
                  }
                />
                {selectedOwners.length > 0 && (
                  <Box>
                    <Divider sx={{ mb: 1 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      CURRENT OWNERS
                    </Typography>
                    <Table size="small" sx={{ mt: 0.5 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Username
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOwners.map((o) => (
                          <TableRow key={o.id}>
                            <TableCell>
                              {o.first_name || o.last_name
                                ? `${o.first_name ?? ""} ${o.last_name ?? ""}`.trim()
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                @{o.username}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )}

            {tab === "users" && !isCreate && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Autocomplete
                  options={addUserOptions}
                  loading={addUserLoading}
                  componentsProps={platformUserAutocompleteComponentsProps}
                  ListboxComponent={PlatformUserAutocompleteListbox}
                  ListboxProps={{ onScroll: addUserListboxOnScroll }}
                  value={null}
                  onChange={handleAddUser}
                  getOptionLabel={formatPlatformUserOptionLabel}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  getOptionDisabled={(option) =>
                    Boolean(option.client_is_archived)
                  }
                  disabled={isSaving || !isSuperUser}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box
                        sx={{
                          width: "100%",
                          py: 0.4,
                          opacity: option.client_is_archived ? 0.45 : 1,
                        }}
                      >
                        <PlatformUserLabelBlock user={option} />
                        {option.client_is_archived ? (
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ display: "block", mt: 0.25 }}
                          >
                            Archived
                          </Typography>
                        ) : option.client_name ? (
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{ display: "block", mt: 0.25 }}
                          >
                            Current Client: {option.client_name}
                          </Typography>
                        ) : null}
                      </Box>
                    </li>
                  )}
                  onInputChange={(_, value) => setAddUserSearch(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Client User"
                      size="small"
                      placeholder="Search users to assign…"
                    />
                  )}
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  ASSIGNED CLIENT USERS (
                  {isLoadingClientUsers
                    ? "\u2026"
                    : clientUsers.length + pendingAddedUsers.length}
                  )
                </Typography>
                {isLoadingClientUsers ? (
                  <Box>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        variant="text"
                        height={40}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Box>
                ) : clientUsers.length === 0 &&
                  pendingAddedUsers.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ py: 2 }}
                  >
                    No users assigned to this client yet.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                          Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                          Username
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                          Roles
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, fontSize: 12, width: 40 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            {u.first_name
                              ? `${u.first_name} ${u.last_name ?? ""}`.trim()
                              : "\u2014"}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              @{u.username}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {u.profile.is_data_storage_user && (
                                <Chip
                                  label="Data Storage"
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: 10,
                                    fontWeight: 700,
                                  }}
                                />
                              )}
                              {u.profile.is_integration_dashboard_user && (
                                <Chip
                                  label="Integration"
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: "#fff3e0",
                                    color: "#e65100",
                                  }}
                                />
                              )}
                              {u.profile.is_ai_agent_user && (
                                <Chip
                                  label="AI Agent"
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: "rgba(46,125,50,0.10)",
                                    color: "#2e7d32",
                                  }}
                                />
                              )}
                              {!u.profile.is_data_storage_user &&
                                !u.profile.is_integration_dashboard_user &&
                                !u.profile.is_ai_agent_user && (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    No roles
                                  </Typography>
                                )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {pendingAddedUsers.map((u) => (
                        <TableRow key={`pending-${u.id}`}>
                          <TableCell>
                            {u.first_name || u.last_name
                              ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()
                              : "\u2014"}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              @{u.username}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Pending"
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: "#e3f2fd",
                                color: "#1565c0",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleRemovePendingUser(u.id)}
                              sx={{
                                color: "error.main",
                                p: 0.25,
                                "&:hover": { bgcolor: "rgba(211,47,47,0.08)" },
                              }}
                            >
                              <Close fontSize="inherit" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}
          </Box>
        </CustomScrollbars>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        {hasUnsavedChanges && (
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: "#ad1457" }}
          >
            Unsaved changes
          </Typography>
        )}
        {!isCreate && client && (
          <Button
            variant="outlined"
            color={client.is_archived ? "success" : "error"}
            onClick={
              client.is_archived
                ? handleUnarchive
                : () => setArchiveDialogOpen(true)
            }
            disabled={isSaving || !isSuperUser}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {client.is_archived ? "Un-Archive" : "Archive"}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSaving || !canSave || !isSuperUser || !hasUnsavedChanges}
          startIcon={
            isSaving ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {isCreate ? "Create Client" : "Save Changes"}
        </Button>
      </DialogActions>

      {/* Archive dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Archive Client
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleArchiveNow}
            disabled={isArchiving}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isArchiving ? "Processing..." : "Archive Immediately"}
          </Button>

          <Divider>
            <Typography variant="caption" color="text.secondary">
              or schedule
            </Typography>
          </Divider>

          <Box>
            <Typography variant="body2" fontWeight={700} mb={1}>
              Schedule archive date
            </Typography>
            {client?.scheduled_archive_date && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Currently scheduled for{" "}
                {(() => {
                  const d = new Date(
                    client.scheduled_archive_date + "T00:00:00",
                  );
                  return d.toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  });
                })()}
              </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Archive on date"
                value={archiveDialogDate}
                onChange={(v) => setArchiveDialogDate(v ?? null)}
                minDate={new Date(Date.now() + 86400000)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { "& .MuiOutlinedInput-root": { borderRadius: 1 } },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              disabled={!archiveDialogDate || isArchiving}
              sx={{
                mt: 1,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1,
                fontSize: 12,
              }}
              onClick={handleScheduleArchive}
            >
              {isArchiving ? "Processing..." : "Set Schedule"}
            </Button>
          </Box>

          {client?.scheduled_archive_date && (
            <Box>
              <Divider sx={{ mb: 2 }} />
              <Button
                variant="text"
                color="error"
                fullWidth
                size="small"
                disabled={isArchiving}
                sx={{ textTransform: "none", fontWeight: 600 }}
                onClick={handleCancelSchedule}
              >
                Cancel existing schedule
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setArchiveDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <AppDialog
        open={saveConfirmOpen}
        onClose={() => setSaveConfirmOpen(false)}
        title={isCreate ? "Confirm Create Client" : "Confirm Save Changes"}
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => setSaveConfirmOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setSaveConfirmOpen(false);
                performSave();
              }}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {isCreate ? "Create Client" : "Save Changes"}
            </Button>
          </>
        }
      >
        {pendingAddedUsers.some((u) => u.client_name) && (
          <Typography
            variant="body2"
            sx={{
              color: "error.main",
              fontWeight: 700,
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "rgba(211,47,47,0.08)",
            }}
          >
            Users reassigned from another client will lose all folder and
            integration access.
          </Typography>
        )}
        {saveConfirmSections.map((section, idx) => (
          <Box key={section.label}>
            {idx > 0 && <Divider sx={{ my: 2 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={0.6}
              display="block"
              sx={{ mb: 1 }}
            >
              {section.label}
            </Typography>
            {section.added && section.added.length > 0 && (
              <Box
                sx={{
                  mb:
                    section.updated?.length ||
                    section.removed?.length ||
                    section.enabled?.length ||
                    section.disabled?.length
                      ? 1.5
                      : 0,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  letterSpacing={0.5}
                  sx={{ color: "success.main", display: "block", mb: 0.5 }}
                >
                  Added
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                >
                  {section.added.map((item) => (
                    <Typography key={item} variant="body2" component="li">
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {section.updated && section.updated.length > 0 && (
              <Box
                sx={{
                  mb:
                    section.removed?.length ||
                    section.enabled?.length ||
                    section.disabled?.length
                      ? 1.5
                      : 0,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  letterSpacing={0.5}
                  sx={{ color: "warning.main", display: "block", mb: 0.5 }}
                >
                  Updated
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                >
                  {section.updated.map((item) => (
                    <Typography key={item} variant="body2" component="li">
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
            {section.removed && section.removed.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  letterSpacing={0.5}
                  sx={{ color: "error.main", display: "block", mb: 0.5 }}
                >
                  Removed
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, "& li": { mb: 0.25 } }}
                >
                  {section.removed.map((item) => (
                    <Typography key={item} variant="body2" component="li">
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </AppDialog>
    </Dialog>
  );
}
